import strings from "global/constants/StringConstants";

/**
 * An in-person signer may hand off with a name only. The backend still needs a
 * unique key for them, so it assigns a generated, undeliverable stand-in on the
 * reserved `@no-email.invalid` domain.
 *
 * That address must never be rendered — it looks like a real email and is not
 * one. Every place a signer email reaches a screen goes through here first.
 * The local part varies per signer, so the test is the domain suffix.
 */
export const isPlaceholderSignerEmail = (email?: string | null): boolean =>
  Boolean(email?.trim().toLowerCase().endsWith(strings.NO_EMAIL_DOMAIN));

/**
 * A signer email safe to render: the address itself, or "" when the signer has
 * no real one. Callers decide what to show in its place (usually nothing, so
 * the row falls back to the name alone).
 */
export const getDisplaySignerEmail = (email?: string | null): string =>
  isPlaceholderSignerEmail(email) ? "" : (email ?? "");

/**
 * Whether a `start` / `next` response actually resolved to an in-person
 * signer, and so may be handed to the person standing at the host's device.
 *
 * A hand-off is only ever legitimate for a signer the requester set to
 * `IN_PERSON`. A session that comes back pointing at an emailed signer means
 * the turn was resolved wrongly — most visibly on a mixed signing-order level
 * (one in-person + one emailed signer), where advancing past the in-person
 * signer must wait for the emailed one rather than opening their document on
 * the host's device. Signing there would attribute a signature collected in
 * the room to someone who was invited by their own private link.
 *
 * Readiness itself is NOT decided here — the server's
 * `InPersonSessionStatus.actionableSigners` is the only authority on who may
 * be handed the device (the launch screen renders that list verbatim). This is
 * the last-line check on what a session actually resolved to.
 *
 * Absent `deliveryMode` means the backend hasn't started sending it yet, so
 * this passes — the guard tightens on its own once the field lands, and never
 * blocks a working session in the meantime.
 */
export const isInPersonSessionSigner = (response?: {
  deliveryMode?: string;
}): boolean =>
  !response?.deliveryMode ||
  response.deliveryMode === strings.deliveryModes.inPerson;
